import { computed } from '@angular/core';
import type { SelectOption } from '@indutrust/shared-model';
import { signalStoreFeature, type, withComputed } from '@ngrx/signals';
import type {
  EntityId,
  EntityProps,
  EntityState,
} from '@ngrx/signals/entities';
import { deepSortArrayProperties } from '../../array';

/**
 * Add derived selectors that return entities ordered by `name` and deep-sorted arrays.
 *
 * Provides `entitiesOrdered` (sorted by name and deep-sorted arrays).
 *
 * @template Entity Entity type with optional `name` and required `id`.
 * @returns A Signal Store feature that can be composed with other features.
 * @example
 * const store = signalStore(
 *   withOrderedEntities<MyEntity>()
 * );
 */
export function withOrderedEntities<
  Entity extends { name?: string; id: EntityId },
>() {
  return signalStoreFeature(
    { state: type<EntityState<Entity>>(), props: type<EntityProps<Entity>>() },
    withComputed(({ entities }) => ({
      entitiesOrdered: computed(() => {
        // Sort entities by their top-level name property
        const sortedEntities = entities().sort((a, b) => {
          return a.name?.localeCompare(b.name ?? '') ?? 0;
        });

        // Deep sort any array properties containing objects with name properties
        return sortedEntities.map((entity) => deepSortArrayProperties(entity));
      }),
    }))
  );
}

/**
 * Computed signal that maps ordered entities to SelectOption items.
 *
 * Use this with {@link withComputed} to extend `entitiesOrdered` with select dropdown formatting.
 * Requires `entitiesOrdered` to be available in the store.
 *
 * @template Entity Entity type with required `id` and `name`.
 * @returns A computed signal definition for use with `withComputed`.
 * @example
 * const store = signalStore(
 *   withOrderedEntities<MyEntity>(),
 *   withComputed(entitiesOrderedAsSelectOptionComputed<MyEntity>())
 * );
 */
export function entitiesOrderedAsSelectOptionComputed<
  Entity extends { name: string; id: EntityId },
>() {
  return ({ entitiesOrdered }: { entitiesOrdered: () => Entity[] }) => ({
    entitiesOrderedAsSelectOption: computed(() =>
      entitiesOrdered().map((entity): SelectOption => ({
        value: entity.id,
        label: entity.name,
      }))
    ),
  });
}
